import {
  EMPTY_RAW_METRICS,
  type MetricPolarity,
  type RawMetricTotals,
  type WorkspaceMetricColumnDef,
} from '@/utils/workspaceAdProductMetrics';

export const AUDIENCE_SEGMENT_IDS = ['new_to_brand'] as const;

export type AudienceSegmentId = (typeof AUDIENCE_SEGMENT_IDS)[number];

export const AUDIENCE_SEGMENT_ROWS: Array<{ id: AudienceSegmentId; label: string }> = [
  { id: 'new_to_brand', label: 'New-to-brand shoppers' },
];

export type AudienceDerivedMetrics = RawMetricTotals & {
  units: number;
  ctr: number;
  cpc: number;
  cr: number;
  acos: number;
  roas: number;
  asp: number;
};

export const EMPTY_AUDIENCE_METRICS: AudienceDerivedMetrics = {
  ...EMPTY_RAW_METRICS,
  units: 0,
  ctr: 0,
  cpc: 0,
  cr: 0,
  acos: 0,
  roas: 0,
  asp: 0,
};

export type AudienceMetricColumnDef = {
  key: keyof AudienceDerivedMetrics;
  label: string;
  polarity: MetricPolarity;
  format: WorkspaceMetricColumnDef['format'];
};

export const AUDIENCE_METRIC_COLUMNS: AudienceMetricColumnDef[] = [
  { key: 'cost', label: 'Spend', polarity: 'lower_is_better', format: 'currency' },
  { key: 'cpc', label: 'CPC', polarity: 'lower_is_better', format: 'currency' },
  { key: 'ctr', label: 'CTR', polarity: 'higher_is_better', format: 'percent' },
  { key: 'cr', label: 'CR%', polarity: 'higher_is_better', format: 'percent' },
  { key: 'purchases30d', label: 'Orders', polarity: 'higher_is_better', format: 'int' },
  { key: 'impressions', label: 'Impressions', polarity: 'higher_is_better', format: 'int' },
  { key: 'clicks', label: 'Clicks', polarity: 'higher_is_better', format: 'int' },
  { key: 'sales30d', label: 'Sales', polarity: 'higher_is_better', format: 'currency' },
  { key: 'units', label: 'Units', polarity: 'higher_is_better', format: 'int' },
  { key: 'acos', label: 'ACoS', polarity: 'lower_is_better', format: 'percent' },
  { key: 'roas', label: 'ROAS', polarity: 'higher_is_better', format: 'ratio' },
  { key: 'asp', label: 'ASP', polarity: 'higher_is_better', format: 'currency' },
];

export type AudienceRawTotals = RawMetricTotals & { units: number };

export function deriveAudienceMetrics(raw: AudienceRawTotals): AudienceDerivedMetrics {
  const { impressions, clicks, cost, purchases30d, sales30d, units } = raw;
  return {
    impressions,
    clicks,
    cost,
    purchases30d,
    sales30d,
    units,
    ctr: impressions > 0 ? (clicks / impressions) * 100 : 0,
    cpc: clicks > 0 ? cost / clicks : 0,
    cr: clicks > 0 ? (purchases30d / clicks) * 100 : 0,
    acos: sales30d > 0 ? (cost / sales30d) * 100 : 0,
    roas: cost > 0 ? sales30d / cost : 0,
    asp: purchases30d > 0 ? sales30d / purchases30d : 0,
  };
}

/** NTB report ingest is not wired yet — always return zero-filled fixed rows. */
export function emptyAudienceMetricsMap(): Map<AudienceSegmentId, AudienceDerivedMetrics> {
  const out = new Map<AudienceSegmentId, AudienceDerivedMetrics>();
  for (const id of AUDIENCE_SEGMENT_IDS) {
    out.set(id, { ...EMPTY_AUDIENCE_METRICS });
  }
  return out;
}

/** Demo profile fixture so Audience is not an empty zero table in demos. */
export function demoAudienceMetricsMap(): Map<AudienceSegmentId, AudienceDerivedMetrics> {
  const out = emptyAudienceMetricsMap();
  out.set(
    'new_to_brand',
    deriveAudienceMetrics({
      impressions: 42_000,
      clicks: 1_680,
      cost: 3_360,
      purchases30d: 252,
      sales30d: 17_640,
      units: 268,
    })
  );
  return out;
}

export const DEMO_PROFILE_ID = 'demo-profile-001';

export function aggregateAudienceMetrics(
  ntbRows: Array<{
    impressions?: number;
    clicks?: number;
    cost?: number;
    purchases30d?: number;
    sales30d?: number;
    units?: number;
  }>
): Map<AudienceSegmentId, AudienceDerivedMetrics> {
  const raw: AudienceRawTotals = {
    ...EMPTY_RAW_METRICS,
    units: 0,
  };
  for (const row of ntbRows) {
    raw.impressions += Number(row.impressions ?? 0);
    raw.clicks += Number(row.clicks ?? 0);
    raw.cost += Number(row.cost ?? 0);
    raw.purchases30d += Number(row.purchases30d ?? 0);
    raw.sales30d += Number(row.sales30d ?? 0);
    raw.units += Number(row.units ?? 0);
  }
  const out = emptyAudienceMetricsMap();
  out.set('new_to_brand', deriveAudienceMetrics(raw));
  return out;
}
